import isNil from 'lodash/isNil';
import { useEffect, useRef, useState } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Typography, TypographyVariant } from '@ui/components/texts/Typography';
import { HintTooltip } from '@ui/components/tooltips/HintTooltip';
import { cn } from '@ui/lib/utils';

interface ActionsMenuActionBase {
  label: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  destructive?: boolean;
  disabled?: boolean;
  hint?: string;
  note?: string;
}

interface ActionsMenuClickAction extends ActionsMenuActionBase {
  onClick: () => void;
}

interface ActionsMenuSubMenu {
  header: string;
  actions: ActionsMenuClickAction[];
}

interface ActionsMenuSubMenuAction extends ActionsMenuActionBase {
  subMenu: ActionsMenuSubMenu;
}

type ActionsMenuAction = ActionsMenuClickAction | ActionsMenuSubMenuAction;

interface ActionsMenuItemsProps {
  actions: ActionsMenuAction[];
  onSelect: () => void;
}

const ACTIONS_MENU_CONTENT_CLASSNAME = 'w-max min-w-56 flex-col gap-0 p-1';

const ITEM_CLASSNAME = [
  'h-auto w-full justify-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal',
  'hover:bg-muted focus-visible:bg-muted',
].join(' ');

const DESTRUCTIVE_ITEM_CLASSNAME = [
  'text-destructive hover:bg-destructive/10 hover:text-destructive',
  'focus-visible:bg-destructive/10 focus-visible:text-destructive',
].join(' ');

const ICON_CLASSNAME = 'size-4 shrink-0';

interface ActionsMenuItemIconProps {
  icon: ActionsMenuAction['icon'];
  reserveSpace: boolean;
}

const ActionsMenuItemIcon = ({ icon: Icon, reserveSpace }: ActionsMenuItemIconProps) => {
  if (!isNil(Icon)) {
    return <Icon className={ICON_CLASSNAME} />;
  }
  if (reserveSpace) {
    return <span aria-hidden className={ICON_CLASSNAME} />;
  }
  return null;
};

const ActionsMenuItems = ({ actions, onSelect }: ActionsMenuItemsProps) => {
  const [openSubMenuLabel, setOpenSubMenuLabel] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const openSubMenuParent = actions.find(({ label }) => label === openSubMenuLabel);
  const openSubMenu = !isNil(openSubMenuParent) && 'subMenu' in openSubMenuParent ? openSubMenuParent.subMenu : null;
  const visibleActions: ActionsMenuAction[] = openSubMenu?.actions ?? actions;
  const hasIcons = visibleActions.some(({ icon }) => !isNil(icon));

  useEffect(() => {
    if (isNil(openSubMenuLabel)) {
      return;
    }
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [openSubMenuLabel]);

  const handleClick = (action: ActionsMenuAction) => {
    if ('subMenu' in action) {
      setOpenSubMenuLabel(action.label);
      return;
    }
    onSelect();
    action.onClick();
  };

  return (
    <Div ref={menuRef} column role="menu" aria-label={openSubMenu?.header}>
      {!isNil(openSubMenu) && (
        <Typography as="span" aria-hidden variant={TypographyVariant.OVERLINE} className="max-w-64 px-2 pt-1.5 pb-1">
          {openSubMenu.header}
        </Typography>
      )}
      {visibleActions.map((action) => {
        const item = (
          <Button
            key={action.label}
            variant={ButtonVariant.GHOST}
            role="menuitem"
            aria-haspopup={'subMenu' in action ? 'menu' : undefined}
            disabled={action.disabled}
            onClick={() => handleClick(action)}
            className={cn(ITEM_CLASSNAME, action.destructive && DESTRUCTIVE_ITEM_CLASSNAME)}
          >
            <ActionsMenuItemIcon icon={action.icon} reserveSpace={hasIcons} />
            {action.label}
          </Button>
        );
        if (isNil(action.hint)) {
          return item;
        }
        return (
          <HintTooltip
            key={action.label}
            content={action.hint}
            note={action.note}
            side="left"
            focusable={action.disabled}
            triggerClassName="flex w-full"
          >
            {item}
          </HintTooltip>
        );
      })}
    </Div>
  );
};

export { ACTIONS_MENU_CONTENT_CLASSNAME, ActionsMenuItems };
export type {
  ActionsMenuAction,
  ActionsMenuClickAction,
  ActionsMenuItemsProps,
  ActionsMenuSubMenu,
  ActionsMenuSubMenuAction,
};
